import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile' | 'video.other';
  ogTitle?: string;
  ogDescription?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  noIndex?: boolean;
  structuredData?: any;
  additionalMeta?: Array<{ name?: string; property?: string; content: string; }>;
  lang?: string;
  alternateHreflang?: Array<{ href: string; hreflang: string; }>;
}

// Enhanced default keywords based on SEO strategy and competitor analysis
const DEFAULT_KEYWORDS = [
  // Core Keywords (High-Intent Learners) - Primary Variations
  'learn farsi', 'learn persian', 'farsi lessons online', 'persian language course',
  'farsi classes near me', 'learn farsi online free', 'persian tutor online',
  'best app to learn farsi', 'farsi for beginners', 'persian alphabet lessons',
  'farsi language learning', 'persian language app', 'farsi course online',
  'learn persian online', 'farsi lessons', 'persian lessons online',
  
  // Core Keywords - Secondary Variations
  'farsi learning app', 'persian learning app', 'farsi language app', 'persian language learning',
  'farsi online course', 'persian online course', 'farsi language course', 'persian language app',
  'learn farsi language', 'learn persian language', 'farsi learning platform', 'persian learning platform',
  'farsi language lessons', 'persian language lessons', 'farsi learning website', 'persian learning website',
  
  // Long-Tail Keywords (Specific Needs) - Primary Variations
  'how to learn farsi fast', 'easiest way to learn persian', 'learn farsi in 30 days',
  'learn persian for travel', 'conversational farsi phrases', 'farsi grammar explained',
  'persian numbers for beginners', 'farsi flashcards online', 'learn to read and write persian',
  'persian language learning podcast', 'farsi pronunciation guide', 'persian alphabet pronunciation',
  'farsi vocabulary builder', 'persian vocabulary list', 'farsi conversation practice',
  
  // Long-Tail Keywords - Secondary Variations
  'fastest way to learn farsi', 'quick farsi learning', 'accelerated persian course',
  'persian for travelers', 'farsi travel phrases', 'iran travel language',
  'farsi grammar rules', 'persian grammar tutorial', 'farsi grammar lessons',
  'persian numbers 1-100', 'farsi counting', 'persian number system',
  'farsi flashcard app', 'persian flashcard deck', 'farsi vocabulary cards',
  'persian reading and writing', 'farsi script learning', 'persian calligraphy',
  'farsi language podcast', 'persian learning audio', 'farsi pronunciation audio',
  'persian alphabet chart', 'farsi letter pronunciation', 'persian script tutorial',
  'farsi vocabulary app', 'persian word list', 'farsi language vocabulary',
  'persian conversation practice', 'farsi speaking practice', 'persian dialogue examples',
  
  // Cultural & Contextual Keywords - Primary Variations
  'learn farsi for business', 'persian poetry and language', 'farsi words in daily life',
  'learn persian through music', 'persian culture and language learning',
  'farsi for heritage learners', 'iranian language learning resources',
  'persian culture immersion', 'farsi cultural context', 'persian traditions and language',
  
  // Cultural & Contextual Keywords - Secondary Variations
  'business farsi', 'persian business language', 'farsi for professionals',
  'persian poetry lessons', 'farsi literature', 'persian poetry analysis',
  'daily farsi phrases', 'persian everyday words', 'farsi common expressions',
  'persian music lessons', 'farsi through songs', 'persian cultural music',
  'heritage farsi learners', 'persian heritage speakers', 'farsi for iranians abroad',
  'iranian culture lessons', 'persian cultural education', 'farsi cultural immersion',
  'persian traditions', 'farsi cultural practices', 'persian customs and language',
  
  // Related SEO Keywords - Primary Variations
  'iranian language lessons', 'dari language learning', 'tajik persian learning',
  'farsi vs persian language', 'arabic vs farsi differences', 'is persian hard to learn',
  'persian language difficulty', 'farsi learning difficulty', 'persian vs arabic',
  
  // Related SEO Keywords - Secondary Variations
  'iranian language courses', 'dari language courses', 'tajik language learning',
  'persian vs farsi difference', 'arabic vs persian comparison', 'persian language complexity',
  'farsi learning challenges', 'persian grammar difficulty', 'farsi pronunciation difficulty',
  'persian alphabet difficulty', 'farsi writing system', 'persian script complexity',
  
  // Competitor Analysis Keywords (from top-ranking sites)
  'farsi language school', 'persian language institute', 'farsi learning center',
  'persian language academy', 'farsi education online', 'persian language education',
  'farsi language program', 'persian language program', 'farsi learning method',
  'persian learning method', 'farsi language system', 'persian language system',
  'farsi language training', 'persian language training', 'farsi language coaching',
  'persian language coaching', 'farsi language mentoring', 'persian language mentoring',
  
  // Mobile & App Keywords
  'farsi learning mobile app', 'persian language app download', 'farsi app for android',
  'persian app for iphone', 'farsi learning app free', 'persian language app free',
  'best farsi app', 'best persian app', 'farsi app review', 'persian app review',
  'farsi learning app ios', 'persian language app android', 'farsi app features',
  'persian app features', 'farsi app download', 'persian app download',
  
  // Free vs Paid Keywords
  'free farsi lessons', 'free persian lessons', 'farsi lessons free',
  'persian lessons free', 'free farsi course', 'free persian course',
  'farsi free trial', 'persian free trial', 'farsi premium course',
  'persian premium course', 'farsi paid lessons', 'persian paid lessons',
  'farsi subscription', 'persian subscription', 'farsi course pricing',
  'persian course pricing', 'farsi learning cost', 'persian learning cost',
  
  // Skill Level Keywords
  'farsi beginner lessons', 'persian beginner lessons', 'farsi intermediate lessons',
  'persian intermediate lessons', 'farsi advanced lessons', 'persian advanced lessons',
  'farsi beginner course', 'persian beginner course', 'farsi intermediate course',
  'persian intermediate course', 'farsi advanced course', 'persian advanced course',
  'farsi for absolute beginners', 'persian for absolute beginners', 'farsi for kids',
  'persian for kids', 'farsi for adults', 'persian for adults',
  
  // Specific Learning Goals
  'farsi speaking practice', 'persian speaking practice', 'farsi listening practice',
  'persian listening practice', 'farsi reading practice', 'persian reading practice',
  'farsi writing practice', 'persian writing practice', 'farsi comprehension',
  'persian comprehension', 'farsi fluency', 'persian fluency', 'farsi proficiency',
  'persian proficiency', 'farsi mastery', 'persian mastery',
  
  // Geographic Keywords
  'farsi lessons usa', 'persian lessons usa', 'farsi lessons canada',
  'persian lessons canada', 'farsi lessons uk', 'persian lessons uk',
  'farsi lessons australia', 'persian lessons australia', 'farsi lessons europe',
  'persian lessons europe', 'farsi lessons online worldwide', 'persian lessons online worldwide',
  
  // Time-based Keywords
  'farsi lessons 24/7', 'persian lessons 24/7', 'farsi anytime learning',
  'persian anytime learning', 'farsi self-paced', 'persian self-paced',
  'farsi flexible schedule', 'persian flexible schedule', 'farsi weekend lessons',
  'persian weekend lessons', 'farsi evening lessons', 'persian evening lessons',
  
  // Technology Keywords
  'farsi online learning', 'persian online learning', 'farsi digital learning',
  'persian digital learning', 'farsi virtual lessons', 'persian virtual lessons',
  'farsi video lessons', 'persian video lessons', 'farsi interactive lessons',
  'persian interactive lessons', 'farsi gamified learning', 'persian gamified learning',
  
  // Community & Social Keywords
  'farsi learning community', 'persian learning community', 'farsi language exchange',
  'persian language exchange', 'farsi study group', 'persian study group',
  'farsi language partner', 'persian language partner', 'farsi conversation partner',
  'persian conversation partner', 'farsi learning forum', 'persian learning forum',
  
  // Certification & Achievement Keywords
  'farsi language certificate', 'persian language certificate', 'farsi proficiency test',
  'persian proficiency test', 'farsi language assessment', 'persian language assessment',
  'farsi learning progress', 'persian learning progress', 'farsi achievement tracking',
  'persian achievement tracking', 'farsi learning milestones', 'persian learning milestones',
  
  // Additional relevant keywords
  'farsi vocabulary', 'persian pronunciation', 'farsi conversation', 'iranian culture',
  'persian script', 'farsi grammar', 'persian language app', 'farsi tutor',
  'persian language learning', 'farsi conversation', 'iran language', 'persian script',
  'farsi alphabet', 'persian alphabet', 'farsi letters', 'persian letters',
  'farsi words', 'persian words', 'farsi phrases', 'persian phrases',
  'farsi sentences', 'persian sentences', 'farsi text', 'persian text',
  'farsi audio', 'persian audio', 'farsi video', 'persian video',
  'farsi exercises', 'persian exercises', 'farsi quizzes', 'persian quizzes',
  'farsi tests', 'persian tests', 'farsi homework', 'persian homework',
  'farsi assignments', 'persian assignments', 'farsi projects', 'persian projects'
];

const SEO: React.FC<SEOProps> = ({
  title = 'پیروز (Pirooz) - Master Farsi Online | Learn Persian Language Fast',
  description = 'Master Farsi (Persian) with پیروز Pirooz - the most comprehensive online platform for learning Persian language. Interactive lessons, vocabulary builder, cultural insights, and expert instruction. Start your journey to fluency today!',
  keywords = DEFAULT_KEYWORDS,
  canonicalUrl,
  ogImage = '/logo512.png',
  ogType = 'website',
  ogTitle,
  ogDescription,
  twitterCard = 'summary_large_image',
  noIndex = false,
  structuredData,
  additionalMeta = [],
  lang = 'en',
  alternateHreflang = []
}) => {
  // Detect current domain
  const currentDomain = window.location.hostname;
  const isProduction = currentDomain === 'farsi-app-8d683.web.app' || currentDomain === 'farsiforfree.web.app';
  const fullCanonicalUrl = canonicalUrl 
    ? `https://${isProduction ? 'farsi-app-8d683.web.app' : currentDomain}${canonicalUrl}`
    : `https://${isProduction ? 'farsi-app-8d683.web.app' : currentDomain}${window.location.pathname}`;
  const fullOgImage = ogImage.startsWith('http') 
    ? ogImage 
    : `https://${isProduction ? 'farsi-app-8d683.web.app' : currentDomain}${ogImage}`;

  // Enhanced default structured data
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "پیروز Pirooz",
    "description": "Comprehensive online Farsi (Persian) language learning platform",
    "url": "https://farsi-app-8d683.web.app",
    "logo": "https://farsi-app-8d683.web.app/logo512.png",
    "foundingDate": "2024",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "support@pingquack.com"
    },
    "sameAs": [
      "https://twitter.com/Pirooz_farsi",
      "https://facebook.com/Pirooz.farsi",
      "https://instagram.com/Pirooz_farsi"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Farsi Language Courses",
      "itemListElement": [
        {
          "@type": "Course",
          "name": "Beginner Farsi Course",
          "description": "Complete beginner course for learning Farsi from scratch",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "پیروز Pirooz"
          },
          "educationalLevel": "Beginner",
          "inLanguage": ["en", "fa"],
          "courseCode": "FARSI-101"
        },
        {
          "@type": "Course", 
          "name": "Persian Alphabet Learning",
          "description": "Master the Persian alphabet and script",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "پیروز Pirooz"
          },
          "educationalLevel": "Beginner",
          "inLanguage": ["en", "fa"],
          "courseCode": "FARSI-ALPHABET"
        },
        {
          "@type": "Course",
          "name": "Farsi Vocabulary Builder", 
          "description": "Expand your Farsi vocabulary with interactive flashcards",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "پیروز Pirooz"
          },
          "educationalLevel": "Beginner to Intermediate",
          "inLanguage": ["en", "fa"],
          "courseCode": "FARSI-VOCAB"
        },
        {
          "@type": "Course",
          "name": "Persian Grammar Fundamentals",
          "description": "Learn essential Persian grammar concepts",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "پیروز Pirooz"
          },
          "educationalLevel": "Intermediate",
          "inLanguage": ["en", "fa"],
          "courseCode": "FARSI-GRAMMAR"
        },
        {
          "@type": "Course",
          "name": "Persian Culture and Conversation",
          "description": "Learn Persian through cultural context and real conversations",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "پیروز Pirooz"
          },
          "educationalLevel": "Intermediate to Advanced",
          "inLanguage": ["en", "fa"],
          "courseCode": "FARSI-CULTURE"
        }
      ]
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://farsi-app-8d683.web.app/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      {/* Basic meta tags */}
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Robots meta */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="پیروز Pirooz" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="fa_IR" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={ogTitle || title} />
      <meta name="twitter:description" content={ogDescription || description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:site" content="@vajak_farsi" />
      <meta name="twitter:creator" content="@vajak_farsi" />

      {/* Additional meta tags */}
      <meta name="author" content="پیروز Pirooz Team" />
      <meta name="publisher" content="پیروز Pirooz" />
      <meta name="rating" content="General" />
      <meta name="distribution" content="Global" />
      <meta name="language" content="English, Persian, Farsi" />
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="United States" />
      
      {/* Language alternates */}
      {alternateHreflang.map((alt, index) => (
        <link key={index} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
      ))}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>

      {/* Additional meta tags */}
      {additionalMeta.map((meta, index) => (
        <meta 
          key={index}
          {...(meta.name ? { name: meta.name } : {})}
          {...(meta.property ? { property: meta.property } : {})}
          content={meta.content}
        />
      ))}
    </Helmet>
  );
};

export default SEO; 